import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useAuth } from "@/hooks/useAuth";
import { getReferralsUIData, subscribeToUserReferralsUIData } from "@/api/referrals";
import { ReferralStatus, type ReferralUIData } from "@/lib/schema";
import { Sidebar } from "@/components/dashboard/Sidebar";
import { ReferralDetailModal } from "@/components/referrals/ReferralDetailModal";
import { ReferralStatusBadge } from "@/components/referrals/ReferralStatusBadge";
import { ReferralsTable } from "@/components/referrals/ReferralsTable";
import { FilterSection, type FilterConfig } from "@/components/ui/FilterSection";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import calendarIcon from "@/assets/jobs/icon_calendar.svg";
import filterIcon from "@/assets/dashboard/filter.svg";

export default function Referrals() {
  const { userData, currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [selectedReferral, setSelectedReferral] = useState<ReferralUIData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [realTimeReferrals, setRealTimeReferrals] = useState<ReferralUIData[]>([]);
  const [useRealTime, setUseRealTime] = useState(false);

  // Get referrals using real API
  const { data: referrals = [], isLoading } = useQuery<ReferralUIData[]>({
    queryKey: ["referrals-ui", userData?.id],
    queryFn: () => getReferralsUIData({ referrerId: userData?.id }),
    enabled: !!userData?.id && !!currentUser,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Set initial data when query succeeds
  useEffect(() => {
    if (referrals.length > 0 && !useRealTime) {
      setRealTimeReferrals(referrals);
      setUseRealTime(true);
    }
  }, [referrals, useRealTime]);

  // Set up real-time subscription with UI data
  useEffect(() => {
    if (!userData?.id) return;

    const unsubscribe = subscribeToUserReferralsUIData(userData.id, (updatedReferrals) => {
      setRealTimeReferrals(updatedReferrals);
    });

    return () => unsubscribe();
  }, [userData?.id]);



  const handleViewReferral = (referral: ReferralUIData) => {
    setSelectedReferral(referral);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReferral(null);
  };

  // Filter configurations for the generic FilterSection
  const statusFilterConfig: FilterConfig = {
    placeholder: "All statuses",
    icon: <img src={filterIcon} alt="Filter" className="h-4 w-4 mr-2 opacity-60" />,
    options: [
      { value: "all", label: "All statuses" },
      { value: "submitted", label: "Submitted" },
      { value: "interviewed", label: "Interviewed" },
      { value: "hired", label: "Hired" },
      { value: "passed_probation", label: "Passed Probation" },
      { value: "rejected", label: "Rejected" }
    ]
  };

  const sortFilterConfig: FilterConfig = {
    placeholder: "Sort by date",
    icon: <img src={calendarIcon} alt="Sort" className="h-4 w-4 mr-2 opacity-60" />,
    options: [
      { value: "date", label: "Sort by date" },
      { value: "name", label: "Sort by name" },
      { value: "status", label: "Sort by status" }
    ]
  };

  // Use real-time data if available, otherwise fall back to initial query data
  const displayReferralsData = useRealTime ? realTimeReferrals : referrals;

  // Apply search and status filtering
  const displayReferrals = displayReferralsData.filter((referral: ReferralUIData) => {
    // Search filtering with regex-based keyword matching
    if (searchTerm.trim()) {
      const searchKeywords = searchTerm.trim().split(/\s+/);
      const searchableText = [
        referral.candidateName,
        referral.jobTitle
      ].join(' ').toLowerCase();
      
      // Check if any keyword matches using regex
      const hasMatch = searchKeywords.some(keyword => {
        try {
          const regex = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
          return regex.test(searchableText);
        } catch {
          // Fallback to simple includes if regex fails
          return searchableText.includes(keyword.toLowerCase());
        }
      });
      
      if (!hasMatch) return false;
    }

    // Status filtering
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'submitted':
          return referral.status === ReferralStatus.SUBMITTED || referral.status === ReferralStatus.PENDING;
        case 'interviewed':
          return referral.status === ReferralStatus.UNDER_REVIEW || 
                 referral.status === ReferralStatus.SHORTLISTED || 
                 referral.status === ReferralStatus.INTERVIEWED;
        case 'hired':
          return referral.status === ReferralStatus.HIRED;
        case 'passed_probation':
          return referral.status === ReferralStatus.PASSED_PROBATION;
        case 'rejected':
          return referral.status === ReferralStatus.REJECTED;
        default:
          return true;
      }
    }

    return true;
  });

  // Apply sorting
  const sortedReferrals = [...displayReferrals].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.candidateName.localeCompare(b.candidateName);
      case 'status':
        return a.status.localeCompare(b.status);
      case 'date':
      default:
        // Assuming referralDate is in a sortable format, otherwise convert to Date
        return new Date(b.referralDate).getTime() - new Date(a.referralDate).getTime();
    }
  });

  // Status counts with separate categories
  const statusCounts = {
    submitted: displayReferralsData.filter((r: ReferralUIData) => 
      r.status === ReferralStatus.SUBMITTED || r.status === ReferralStatus.PENDING
    ).length,
    interviewed: displayReferralsData.filter((r: ReferralUIData) => 
      r.status === ReferralStatus.UNDER_REVIEW || 
      r.status === ReferralStatus.SHORTLISTED || 
      r.status === ReferralStatus.INTERVIEWED
    ).length,
    hired: displayReferralsData.filter((r: ReferralUIData) => r.status === ReferralStatus.HIRED).length,
    passed_probation: displayReferralsData.filter((r: ReferralUIData) => r.status === ReferralStatus.PASSED_PROBATION).length,
    rejected: displayReferralsData.filter((r: ReferralUIData) => r.status === ReferralStatus.REJECTED).length,
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen page-gradient">
        <Sidebar />
        <div className="lg:ml-56 p-4 lg:p-8 pt-20 lg:pt-8">
          {/* Header */}
          <DashboardHeader 
            title="All Referrals" 
            subtitle="Track the status of all your referrals." 
          />

          {/* Status Filter Badges */}
          <div className="flex flex-wrap gap-3 mb-6">
            <ReferralStatusBadge status={ReferralStatus.SUBMITTED} count={statusCounts.submitted} />
            <ReferralStatusBadge status={ReferralStatus.INTERVIEWED} count={statusCounts.interviewed} />
            <ReferralStatusBadge status={ReferralStatus.HIRED} count={statusCounts.hired} />
            <ReferralStatusBadge status={ReferralStatus.PASSED_PROBATION} count={statusCounts.passed_probation} />
            <ReferralStatusBadge status={ReferralStatus.REJECTED} count={statusCounts.rejected} />
          </div>

          {/* Search and Filters */}
          <FilterSection
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search by candidate name or job title"
            filters={{
              status: {
                value: statusFilter,
                onChange: setStatusFilter,
                config: statusFilterConfig
              },
              sort: {
                value: sortBy,
                onChange: setSortBy,
                config: sortFilterConfig
              }
            }}
          />

          {/* Referrals Table */}
          <ReferralsTable
            referrals={sortedReferrals}
            onViewReferral={handleViewReferral}
            itemsPerPage={10}
            showPagination={true}
            isLoading={isLoading}
          />

          {/* Referral Detail Modal */}
          {selectedReferral && (
            <ReferralDetailModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              referral={selectedReferral}
            />
          )}
        </div>
      </div>
    </ProtectedRoute>
  );
}